import React, { useEffect, useMemo } from 'react';
import { useTheme } from '@mui/styles';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryContainer,
  VictoryStack,
  VictoryTooltip
} from 'victory';
import { Grid, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';
import { getFormattedDuration } from 'modules/notes/common/components/Timer/useFormattedDuration';
import { NoteInsightsViewOptions } from 'modules/Insights/Notes/NoteInsightsContextProvider';
import { domainPaddingType, useChartStyles } from './hooks/useChartStyles';
import Footer from './Footer';
import { useMyUtilizationDataPagination } from './hooks/useMyUtilizationDataPagination';
import { dataType } from './utils';

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    paddingTop: theme.spacing(4)
  }
}));

const MyCustomFlyout: React.FC<any> = ({
  x,
  y,
  name,
  datum
}: {
  x: number;
  y: number;
  name: string;
  datum: any;
}) => {
  const { formatMessage } = useIntl();

  const formattedTime = getFormattedDuration({
    formatMessage,
    duration: datum.totalDuration ?? 0,
    needsMask: true
  });
  return (
    <foreignObject x={x} y={y - 40} width="90" height="50">
      <Tooltip title={`${name}: ${formattedTime}`} open={true}>
        <span />
      </Tooltip>
    </foreignObject>
  );
};

type UtilizationChartProp = {
  selectedViewOptionSupervisor: NoteInsightsViewOptions;
  width: number | undefined;
  tData: any[];
  setCurrentChartData: React.Dispatch<React.SetStateAction<dataType[]>>;
};

const UtilizationChart: React.FC<UtilizationChartProp> = ({
  width,
  tData,
  selectedViewOptionSupervisor,
  setCurrentChartData
}: UtilizationChartProp) => {
  const {
    onNext,
    onPrevious,
    showNext,
    showPrev,
    currentData,
    dateSet
  } = useMyUtilizationDataPagination(tData, selectedViewOptionSupervisor, 30);
  useEffect(() => {
    setCurrentChartData(currentData);
  }, [currentData, setCurrentChartData]);
  const theme = useTheme();
  const style = useChartStyles(theme);
  const classes = useStyles();
  const barStyling = useMemo(() => {
    const noOfBars = currentData[0]?.data.length || 0;
    const widthContainer = (width || 0) - 100;
    let bWidth = 0;
    let dPadding = {};
    if (noOfBars <= 4) {
      {
        bWidth = ((widthContainer || 0) - 40 * 4) / 4;
        dPadding = { x: [85, 85], y: [0, 10] };
      }
    } else if (noOfBars <= 7) {
      bWidth = ((widthContainer || 0) - 13 * 7) / 7;
      dPadding = { x: [45, 45], y: [0, 10] };
    } else if (noOfBars <= 13) {
      bWidth = ((widthContainer || 0) - 9 * 13) / 13;
      dPadding = { x: [10, 10], y: [0, 10] };
    } else if (noOfBars <= 20) {
      {
        bWidth = ((widthContainer || 0) - 6 * 20) / 20;
        dPadding = { x: [0, 0], y: [0, 10] };
      }
    } else if (noOfBars <= 25) {
      {
        bWidth = ((widthContainer || 0) - 4.8 * 25) / 25;
        dPadding = { x: [0, 0], y: [0, 10] };
      }
    } else if (noOfBars <= 30) {
      {
        bWidth = ((widthContainer || 0) - 4 * 30) / 30;
        dPadding = { x: [0, 0], y: [0, 10] };
      }
    }
    return {
      barWidth: bWidth,
      domainPadding: dPadding as domainPaddingType
    };
  }, [currentData, width]);
  const tickValues = useMemo(() => dateSet.map((_z, index) => index + 1), [
    dateSet
  ]);

  const tickFormat = useMemo(
    () => dateSet.map((z) => DateTime.fromISO(z, { zone: 'utc' }).day),
    [dateSet]
  );

  return (
    <>
      <Grid
        item
        container
        className={classes.chartContainer}
        data-testid={'my-utilization-chart'}
      >
        <VictoryChart
          domainPadding={barStyling.domainPadding}
          height={style.VictoryChart.height}
          width={width}
          padding={style.VictoryChart.padding}
          containerComponent={<VictoryContainer responsive={true} />}
        >
          <VictoryAxis
            tickValues={tickValues}
            tickFormat={tickFormat}
            style={style.VictoryAxis}
          />
          <VictoryAxis
            dependentAxis
            tickFormat={(x) => `${(x / 3600).toFixed(2)}h`}
            style={style.VictoryAxis}
          />
          {
            <VictoryStack domainPadding={{ x: 10 }}>
              {currentData.map((x, _index) => {
                return (
                  <VictoryBar
                    key={x.name}
                    barWidth={barStyling.barWidth}
                    data={x.data}
                    style={{
                      data: { fill: x.color }
                    }}
                    x="date"
                    y="totalDuration"
                    labels={() => ''}
                    labelComponent={
                      <VictoryTooltip
                        flyoutPadding={10}
                        flyoutComponent={<MyCustomFlyout name={x.name} />}
                      />
                    }
                    events={[
                      {
                        target: 'data',
                        eventHandlers: {
                          onMouseOver: () => {
                            return [
                              {
                                target: 'data',
                                mutation: () => {
                                  return {
                                    style: {
                                      fill: currentData[_index].color,
                                      stroke:
                                        theme.palette.information.background,
                                      strokeWidth: 1,
                                      cursor: 'pointer'
                                    }
                                  };
                                }
                              },
                              {
                                target: 'labels',
                                mutation: () => ({ active: true })
                              }
                            ];
                          },
                          onMouseLeave: () => {
                            return [
                              {
                                target: 'data',
                                mutation: () => {
                                  return {
                                    style: {
                                      fill: currentData[_index].color,
                                      strokeWidth: 0
                                    }
                                  };
                                }
                              },
                              {
                                target: 'labels',
                                mutation: () => ({ active: undefined })
                              }
                            ];
                          }
                        }
                      }
                    ]}
                  />
                );
              })}
            </VictoryStack>
          }
        </VictoryChart>
      </Grid>

      <Footer
        onNext={onNext}
        onPrevious={onPrevious}
        showNext={showNext}
        showPrev={showPrev}
      />
    </>
  );
};

export default React.memo(UtilizationChart);
